import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Card, Label, Spinner, TextInput } from "flowbite-react";
import { Key, User } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { IllustrationAcceptInvite } from "../../../../assets/Illustrations";
import brandLogo from "../../../../assets/images/Logo.png";
import Loading from "../../../../components/Loading";
import PasswordInput from "../../../../components/PasswordInput";
import { ILoginResponse, UserRole } from "../../../../interfaces";
import { httpService } from "../../../../services/httpService";
import { API_CONFIG } from "../../../../shared/constants";
import { useAuth } from "../../../../hooks";

interface FormData {
  name: string;
  password: string;
  confirmPassword: string;
}

interface InviteData {
  uidb64: string;
  token: string;
}

interface IUserDetails {
  is_exist: boolean;
  is_active: boolean;
  is_invited: boolean;
  is_owner: boolean;
  invited_status: "active" | "pending" | "rejected";
}

const AcceptInvite: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permittedRoutes, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDetails, setUserDetails] = useState<IUserDetails | null>(null);

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("configurations.team.acceptInvite.nameRequired")),
    password: yup
      .string()
      .required(t("configurations.team.acceptInvite.passwordRequired"))
      .min(8, t("configurations.team.acceptInvite.passwordMinLength")),
    confirmPassword: yup
      .string()
      .required(t("configurations.team.acceptInvite.confirmPasswordRequired"))
      .oneOf(
        [yup.ref("password")],
        t("configurations.team.acceptInvite.passwordsDoNotMatch")
      ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver<FormData>(schema),
    mode: "onChange",
  });

  useEffect(() => {
    const uidb64 = searchParams.get("uidb64");
    const token = searchParams.get("token");
    const email = searchParams.get("email");

    if (!uidb64 || !token || !email) {
      setError(t("auth.invalidInvite"));
      setIsLoading(false);
      return;
    }

    try {
      setInviteData({ uidb64, token });
      // Fetch user details using email
      httpService
        .get<IUserDetails>(
          API_CONFIG.path.userDetailsFromEmail,
          { email },
          { headers: { Authorization: undefined } } // don't send auth header for login
        )
        .then((response) => {
          setUserDetails(response);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching user details:", error);
          setIsLoading(false);
        });
    } catch (error) {
      console.error("Error decoding invite data:", error);
      setError(t("auth.invalidInvite"));
      setIsLoading(false);
    }
  }, [searchParams, t]);

  const onSubmit = async (data: FormData) => {
    try {
      const uidb64 = inviteData?.uidb64;
      const token = inviteData?.token;

      if (!uidb64 || !token) {
        setError(t("auth.invalidInvite"));
        return;
      }
      setIsSubmitting(true);
      httpService
        .post<ILoginResponse>(
          `${API_CONFIG.path.acceptInvite}/${uidb64}/${token}`,
          {
            name: data.name,
            password: data.password,
            confirmPassword: data.confirmPassword,
          }
        )
        .then((response) => {
          const { user, access, refresh } = response;
          const userData = { ...user, role: "sub_admin" as UserRole };
          httpService.setTokens({ access, refresh });
          httpService.setUserData(userData);
          setUser(userData);
          setIsSubmitting(false);

          // Check if user is owner and redirect accordingly
          if (userDetails?.is_owner) {
            window.location.href = "/integrations?trackingId=add";
          } else {
            window.location.href =
              (permittedRoutes[0] || {})?.path || "/dashboard";
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          console.error(" error:", error);
          toast.error(error.message);
        });
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t("auth.acceptInviteError"));
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-200 via-indigo-300 to-white">
        <Loading className="h-72" />
      </div>
    );
  }

  if (error || !inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-200 via-indigo-100 to-white">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-8 animate-fade-in">
            <img
              src={brandLogo}
              alt="Logo"
              className="h-16 mb-4 cursor-pointer"
              onClick={() => navigate("/")}
            />
          </div>
          <Card className="w-full relative z-10 shadow-2xl border-none bg-white/90 backdrop-blur-sm animate-fade-in-up">
            <div className="text-center p-8">
              <Key className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t("configurations.team.acceptInvite.invalidInvite")}
              </h2>
              <p className="text-gray-600">
                {error ||
                  t("configurations.team.acceptInvite.inviteExpiredOrInvalid")}
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-200 via-indigo-100 to-white relative overflow-hidden">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center mb-4 md:mb-8 animate-fade-in">
          <img
            src={brandLogo}
            alt="Logo"
            className="h-10 md:h-16 mb-2 md:mb-4 cursor-pointer"
            onClick={() => navigate("/")}
          />
        </div>

        <Card
          className="w-full relative z-10 shadow-2xl border-none bg-white/90 backdrop-blur-sm animate-fade-in-up"
          theme={{
            root: {
              children:
                "flex min-h-[400px] flex-col justify-center gap-4 p-3 md:p-6",
            },
          }}
        >
          <div className="flex flex-col md:flex-row">
            {/* Left side - Illustration - Hidden on mobile */}
            <div className="hidden md:flex w-1/2 p-8 items-center justify-center border-r border-gray-200 animate-fade-in-left">
              <div className="flex flex-col items-center justify-center gap-4">
                <IllustrationAcceptInvite className="h-full w-full" />
              </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full md:w-1/2 p-4 md:p-8 md:pl-12 flex flex-col justify-center animate-fade-in-right">
              <div className="text-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {t("configurations.team.acceptInvite.title")}
                </h2>
                <p className="text-sm text-gray-600">
                  {t("configurations.team.acceptInvite.welcomeMessage")}
                </p>
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-4 md:space-y-6"
              >
                <div>
                  <Label
                    htmlFor="name"
                    value={t("configurations.team.acceptInvite.name")}
                    className="text-gray-700"
                  />
                  <TextInput
                    id="name"
                    type="text"
                    icon={User}
                    placeholder={t("configurations.team.acceptInvite.name")}
                    {...register("name")}
                    color={errors.name ? "failure" : undefined}
                    helperText={errors.name?.message}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="password"
                    value={t("configurations.team.acceptInvite.password")}
                    className="text-gray-700"
                  />
                  <PasswordInput
                    id="password"
                    {...register("password")}
                    placeholder={t("configurations.team.acceptInvite.password")}
                    color={errors.password ? "failure" : undefined}
                    helperText={errors.password?.message}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="confirmPassword"
                    value={t(
                      "configurations.team.acceptInvite.confirmPassword"
                    )}
                    className="text-gray-700"
                  />
                  <PasswordInput
                    id="confirmPassword"
                    {...register("confirmPassword")}
                    placeholder={t(
                      "configurations.team.acceptInvite.confirmPassword"
                    )}
                    color={errors.confirmPassword ? "failure" : undefined}
                    helperText={errors.confirmPassword?.message}
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  color="indigo"
                  className="w-full transition-all duration-200 hover:scale-[1.02]"
                  disabled={isSubmitting}
                >
                  <div className="flex items-center gap-2">
                    {isSubmitting && <Spinner color={"white"} size={"sm"} />}
                    <p>
                      {isSubmitting
                        ? t("configurations.team.acceptInvite.submitting")
                        : t("configurations.team.acceptInvite.submit")}
                    </p>
                  </div>
                </Button>
              </form>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvite;
